import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "../ui/textarea";
import {
  appendNoDuplicateRecordNumberRefine,
  multipleprintSchema,
} from "@/api/multipleprint/multipleprint-validation";
import { useCreateMultipleprint } from "@/api/multipleprint/multipleprint";

const schema = appendNoDuplicateRecordNumberRefine(
  multipleprintSchema.pick({
    estampesMultiplesDescriptionNumeroEnregistrement: true,
    estampesMultiplesDescriptionTitre: true,
  }),
);

type FormType = z.infer<typeof schema>;

type NewMultiprintDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewMultiprintDialog = ({
  onOpenChange,
  open,
}: NewMultiprintDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estampesMultiplesDescriptionNumeroEnregistrement: "",
      estampesMultiplesDescriptionTitre: "",
    },
    reValidateMode: "onSubmit",
  });
  const { mutateAsync } = useCreateMultipleprint();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const result = await mutateAsync(data);
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      navigate(`/edit/multipleprint/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("estampesMultiplesDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createMmultipleprint")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="estampesMultiplesDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("estampesMultiplesDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="estampesMultiplesDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("estampesMultiplesDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewMultiprintDialog;
